import { useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { getEnecoBeXapiSiteApiV1AddressesSearchOnZipcode, LanguageCodeDto } from '@eneco/dc-be/client';
import usePublicDCBE from '@eneco/dc-be/hooks/usePublicDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { Button, Heading, RadioGroup, RadioTile, Stack, Stretch, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { Controller, useFormContext } from 'react-hook-form';

import { METER_TYPE_OPTIONS, PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const MeterTypeCheckStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const {
    control,
    trigger,
    setValue,
    formState: { errors },
    getValues,
    watch,
  } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const onNextClick = async () => {
    const hasMeterSelected = await trigger('meterType');
    if (hasMeterSelected) {
      trackEventBE('pbt_calculation_known_meter', {
        status: 'click',
        data: getTrackableFormValues(getValues()),
      });
      nextMainStep();
    }
  };

  const postalCode = watch('postalCode');

  const { data: zipCodeCitySearchDataUnwrapped } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1AddressesSearchOnZipcode,
    {
      query: {
        zipcodepart: postalCode ?? '',
        language: 'NL' as LanguageCodeDto,
      },
    },
    ['/addresses/searchOnZipcode', postalCode ?? ''],
  );

  const zipCodeCitySearchData = unwrapData(zipCodeCitySearchDataUnwrapped);
  const isPostalCodeInWalloon = zipCodeCitySearchData?.addresses?.[0]?.region === 'Walloon';

  useEffect(() => {
    setValue('regionInBelgium', zipCodeCitySearchData?.addresses?.[0]?.region);
  }, [setValue, zipCodeCitySearchData]);

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.digitalMeterQuestionStep.title.value}
        </Heading>
        <Text>{fields.digitalMeterQuestionStep.subTitle.value}</Text>
      </Stack>
      <Stretch>
        <Stack gap={4} direction={'column'}>
          <Controller
            control={control}
            name={'meterType'}
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                name={'meterType'}
                aria-labelledby={fields.digitalMeterQuestionStep.digitalMeterOwnershipOptionsList.value.label}
                direction="column"
                value={value?.toString()}
                error={
                  errors.meterType?.message
                    ? fields.digitalMeterQuestionStep.digitalMeterOwnershipOptionsList.value.requiredMessage
                    : undefined
                }
                onValueChange={onChange}>
                {fields.digitalMeterQuestionStep.digitalMeterOwnershipOptionsList.value.options.map(meterType => {
                  if (meterType.name === METER_TYPE_OPTIONS['BI-DIRECTIONAL'] && !isPostalCodeInWalloon) return null;

                  return (
                    <RadioTile value={meterType.name} key={meterType.name}>
                      <Stack direction="row" gap="2" alignY="center">
                        <Text>{meterType.label}</Text>
                      </Stack>
                    </RadioTile>
                  );
                })}
              </RadioGroup>
            )}
          />
          {isPostalCodeInWalloon && (
            <label htmlFor={'meterType'}>
              <Stack>
                <Heading as="h2" size="3XS">
                  {fields.digitalMeterQuestionStep.explanationTitle.value}
                </Heading>
                <RichText html={fields.digitalMeterQuestionStep.explanationDescription.value} />
              </Stack>
            </label>
          )}
        </Stack>
      </Stretch>
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};
